import React, { useEffect, useMemo, useState } from "react";
import { Modal, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { SessionContext } from "../../../../contexts";
import {
  TGetPatientsResponse,
  TPatientInfo,
  getPatients,
} from "../../../../services/api-clinic";
import { TClinicPatientType } from "../../../../utils/types";
import {
  NormalText,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
} from "../../../common/native";

type AssignImageToPatientModalProps = {
  visible: boolean;
  title?: string;
  onClose: () => void;
  onAssign: (patient: TPatientInfo) => Promise<void> | void;
};

const AssignImageToPatientModal: React.FC<AssignImageToPatientModalProps> = ({
  visible,
  title = "Assign images to a patient",
  onClose,
  onAssign,
}) => {
  const session = React.useContext(SessionContext)?.session;
  const [searchTerm, setSearchTerm] = useState("");
  const [patients, setPatients] = useState<TGetPatientsResponse>([]);
  const [selectedPatientUuid, setSelectedPatientUuid] = useState<string | null>(
    null,
  );
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (!visible || !session) return;

    const fetchPatients = async () => {
      try {
        setIsLoadingPatients(true);
        const response = await getPatients(session, [
          TClinicPatientType.PATIENT,
          TClinicPatientType.LEAD,
          TClinicPatientType.CONSULT,
        ]);
        setPatients(response);
      } catch (error) {
        console.error("Error fetching patients for assignment:", error);
        setPatients([]);
      } finally {
        setIsLoadingPatients(false);
      }
    };

    fetchPatients();
  }, [session, visible]);

  useEffect(() => {
    if (!visible) {
      setSearchTerm("");
      setSelectedPatientUuid(null);
      setIsAssigning(false);
    }
  }, [visible]);

  const filteredPatients = useMemo(() => {
    const lowerCaseTerms = searchTerm.toLowerCase().trim().split(/\s+/);
    if (!searchTerm.trim()) return patients;

    return patients.filter((patient) => {
      const combinedInfo =
        `${patient.firstName} ${patient.lastName} ${patient.email} ${patient.phone}`.toLowerCase();
      return lowerCaseTerms.every((term) => combinedInfo.includes(term));
    });
  }, [patients, searchTerm]);

  const selectedPatient = useMemo(
    () => patients.find((patient) => patient.uuid === selectedPatientUuid),
    [patients, selectedPatientUuid],
  );

  const handleAssign = async () => {
    if (!selectedPatient || isAssigning) return;
    try {
      setIsAssigning(true);
      await onAssign(selectedPatient);
      onClose();
    } catch (error) {
      console.error("Error assigning images to patient:", error);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.modalOverlay} onPress={onClose}>
        <Pressable style={styles.modalCard} onPress={() => null}>
          <NormalText text={title} fontWeight="600" style={styles.title} />

          <SearchInput
            placeholder="Search for a patient"
            value={searchTerm}
            onChangeText={setSearchTerm}
            containerStyle={styles.searchInput}
          />

          <ScrollView
            style={styles.patientList}
            showsVerticalScrollIndicator={false}
          >
            {isLoadingPatients ? (
              <NormalText text="Loading patients..." style={styles.emptyText} />
            ) : filteredPatients.length === 0 ? (
              <NormalText
                text="No matching patients found."
                style={styles.emptyText}
              />
            ) : (
              filteredPatients.map((patient) => {
                const isSelected = patient.uuid === selectedPatientUuid;
                return (
                  <Pressable
                    key={patient.uuid}
                    style={[
                      styles.patientRow,
                      isSelected && styles.patientRowSelected,
                    ]}
                    onPress={() => setSelectedPatientUuid(patient.uuid)}
                  >
                    <View>
                      <NormalText
                        text={`${patient.firstName} ${patient.lastName}`.trim()}
                        fontWeight="600"
                        style={styles.patientName}
                      />
                      <NormalText
                        text={patient.email || ""}
                        style={styles.meta}
                      />
                      <NormalText
                        text={patient.phone || ""}
                        style={styles.meta}
                      />
                    </View>
                  </Pressable>
                );
              })
            )}
          </ScrollView>

          <View style={styles.actions}>
            <SecondaryButton
              text="Cancel"
              width={120}
              roundBorderRadius
              onPress={onClose}
            />
            <PrimaryButton
              text="Assign"
              width={120}
              roundBorderRadius
              loading={isAssigning}
              disabled={!selectedPatient || isLoadingPatients}
              onPress={handleAssign}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 520,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    gap: 12,
  },
  title: {
    fontSize: 20,
    color: "#111827",
    textAlign: "center",
  },
  searchInput: {
    borderWidth: 1,
    borderColor: "#DDDEE0",
    backgroundColor: "#FFFFFF",
  },
  patientList: {
    maxHeight: 260,
  },
  patientRow: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
    backgroundColor: "#FFFFFF",
  },
  patientRowSelected: {
    borderColor: "#00B7F4",
    backgroundColor: "#F0FBFF",
  },
  patientName: {
    color: "#111827",
    marginBottom: 2,
  },
  meta: {
    color: "#6B7280",
    fontSize: 12,
  },
  emptyText: {
    color: "#6B7280",
    textAlign: "center",
    paddingVertical: 16,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 4,
  },
});

export default AssignImageToPatientModal;
